import {

  createContext,

  useCallback,

  useContext,

  useEffect,

  useMemo,

  useRef,

  useState,

} from "react";

import {

  getStoredBaasConsoleAccountId,

  setStoredBaasConsoleAccountId,

} from "../Constants/accountTypes";

import {

  buildBaasInstanceSelectionId,

  createBaasInstance,

  fetchBaasInstances,

  getBaasInstanceById,

  resolveBaasCreateParentInstance,

  updateMockBaasInstance,
  updateBaasInstance,

} from "../Services/baasInstancesApi";

import {
  dedupeAccountsByStableId,
  fetchBaasAccounts,
  getAccountStableId,
  getPrimaryBalance,
  isValutSegregationWalletInstance,
} from "../Services/baasAccountsListApi";
import { formatMonetaryValue } from "../Utils/formatters";
import {
  BAAS_LIST_FILTER_ALL,
} from "../Pages/Private/BaasConsole/baasInstanceListFilter";
import { resolveBaasInstanceCard } from "../Pages/Private/BaasConsole/baasPeerOpsPin";

/** Console `/baas` account options — instance-scoped via MobileService `/v1/ib/console/*` → BaaS. */
const mapConsoleAccountOption = (account) => {
  const primary = getPrimaryBalance(account.balances);
  const stableId = getAccountStableId(account);
  const number = account.accountNumber || account.alias || stableId;
  const name = account.alias || account.entityName || number;
  const balanceHint = primary
    ? formatMonetaryValue(primary.available, primary.currency)
    : null;

  return {
    id: stableId,
    label: name,
    secondaryLabel: [account.entityName, number].filter(Boolean).join(" · "),
    balanceHint,
    accountNumber: account.accountNumber || "",
    entityName: account.entityName || "",
    entityId: account.entityId || "",
    type: account.type || "",
    role: account.type || "",
    balances: account.balances || [],
    raw: account,
  };
};

const STORAGE_KEY = "@WalletBC:baasInstance";



const readStoredInstanceId = () => {

  try {

    return localStorage.getItem(STORAGE_KEY) || null;

  } catch {

    return null;

  }

};



const writeStoredInstanceId = (instanceId) => {

  try {

    if (instanceId) {

      localStorage.setItem(STORAGE_KEY, instanceId);

    } else {

      localStorage.removeItem(STORAGE_KEY);

    }

  } catch {

    // ignore storage errors

  }

};



const BaasInstanceContext = createContext(null);



export const BaasInstanceProvider = ({ children }) => {

  const [instances, setInstances] = useState([]);

  const [selectedInstanceId, setSelectedInstanceId] = useState(readStoredInstanceId);
  // Matrix default: list full network; optional filter narrows (not a prerequisite).
  const [listInstanceFilter, setListInstanceFilter] = useState(BAAS_LIST_FILTER_ALL);
  const hydratedDefaultPinRef = useRef(false);
  /**
   * Soft peer backoffice context: X-Instance-Id / API target without switching the
   * sidebar pin (Valut stays selected). Cleared on Voltar / Root / sidebar switch.
   */
  const [peerOpsInstanceId, setPeerOpsInstanceId] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [accounts, setAccounts] = useState([]);

  const [accountsLoading, setAccountsLoading] = useState(false);

  const [selectedAccountId, setSelectedAccountId] = useState(getStoredBaasConsoleAccountId);

  /** Bumps on every instance switch so console pages can force remount/refetch. */
  const [dataEpoch, setDataEpoch] = useState(0);
  const selectedInstanceIdRef = useRef(selectedInstanceId);
  selectedInstanceIdRef.current = selectedInstanceId;



  const refreshInstances = useCallback(async () => {

    setLoading(true);

    setError(null);

    try {

      const next = await fetchBaasInstances();

      setInstances(next);



      setSelectedInstanceId((current) => {
        const matrix = next.find((instance) => isValutSegregationWalletInstance(instance));
        if (matrix && !hydratedDefaultPinRef.current) {
          hydratedDefaultPinRef.current = true;
          const currentRow = current
            ? next.find((instance) => instance.id === current)
            : null;
          if (!currentRow || !isValutSegregationWalletInstance(currentRow)) {
            writeStoredInstanceId(matrix.id);
            setListInstanceFilter(BAAS_LIST_FILTER_ALL);
            return matrix.id;
          }
        }

        if (current && next.some((instance) => instance.id === current)) {

          return current;

        }



        if (current && !next.some((instance) => instance.id === current)) {

          writeStoredInstanceId(null);

          return null;

        }



        return current;

      });



      return next;

    } catch (err) {

      setError(err);

      setInstances([]);

      throw err;

    } finally {

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    let cancelled = false;



    (async () => {

      try {

        await refreshInstances();

      } catch {

        if (!cancelled) {

          // error already stored in state

        }

      }

    })();



    return () => {

      cancelled = true;

    };

  }, [refreshInstances]);



  const selectedInstance = useMemo(

    () => instances.find((instance) => instance.id === selectedInstanceId) ?? null,

    [instances, selectedInstanceId],

  );

  /** Instance used for console API calls — peer ops override or sidebar pin. */
  const opsInstance = useMemo(() => {
    if (peerOpsInstanceId) {
      const peer = instances.find((instance) => instance.id === peerOpsInstanceId);
      if (peer) return peer;
    }
    return selectedInstance;
  }, [instances, peerOpsInstanceId, selectedInstance]);

  const clearPeerOpsPin = useCallback(() => {
    setPeerOpsInstanceId(null);
  }, []);



  // Metadata-only list (no per-account BaaS balance hydration). Callers that
  // need live saldo should fetch the selected account via …/balances.
  const refreshAccounts = useCallback(async () => {

    if (!selectedInstance) {

      setAccounts([]);

      setAccountsLoading(false);

      return [];

    }



    setAccountsLoading(true);

    try {

      const next = dedupeAccountsByStableId(
        (await fetchBaasAccounts(selectedInstance, { includeBalances: false }).catch(() => [])).map(
          mapConsoleAccountOption,
        ),
      );

      setAccounts(next);



      setSelectedAccountId((current) => {
        const wanted = current != null && current !== "" ? String(current) : null;
        if (wanted && next.some((account) => String(account.id) === wanted)) {
          return wanted;
        }

        // Drop stale selection from another instance card — keeping it made Pix/Extrato
        // call BaaS with the previous tenant's account id after a sidebar switch.
        if (wanted) {
          setStoredBaasConsoleAccountId(null);
        }

        const firstId = next[0]?.id != null ? String(next[0].id) : null;
        if (firstId) {
          setStoredBaasConsoleAccountId(firstId);
          return firstId;
        }
        return null;
      });



      return next;

    } catch {

      setAccounts([]);

      return [];

    } finally {

      setAccountsLoading(false);

    }

  }, [selectedInstance]);



  // Do not auto-hydrate every console account on IB home/PrivateApp mount —
  // that caused N BaaS balance calls and congested the home/saldo path.
  // Console pages that need the list call refreshAccounts() explicitly.



  const selectInstance = useCallback((instanceId, options = {}) => {
    const nextId = instanceId || null;
    writeStoredInstanceId(nextId);

    // Sidebar / explicit switch always drops soft peer ops override.
    if (!options?.preservePeerOpsOverride) {
      setPeerOpsInstanceId(null);
    }

    if (selectedInstanceIdRef.current === nextId) {
      return;
    }

    // Drop previous instance accounts/selection so Contas/Pix/etc. cannot
    // keep showing the last instance while the sidebar already switched.
    setAccounts([]);
    setSelectedAccountId(null);
    setStoredBaasConsoleAccountId(null);
    setDataEpoch((epoch) => epoch + 1);
    selectedInstanceIdRef.current = nextId;
    setSelectedInstanceId(nextId);

    // Matrix pin → default network list ("Todas").
    setListInstanceFilter(BAAS_LIST_FILTER_ALL);
  }, []);

  /**
   * Soft-target a peer tenant for backoffice APIs without changing the sidebar pin.
   * Keeps Valut selected so Voltar does not leave the operator stuck on the client.
   */
  const selectInstanceForPeerOps = useCallback(
    (baasInstanceId, environment = null) => {
      const current = instances.find((row) => row.id === selectedInstanceIdRef.current)
        ?? null;
      const peerCard = resolveBaasInstanceCard(
        instances,
        baasInstanceId,
        environment || current?.environment,
      );
      if (!peerCard?.id) {
        setPeerOpsInstanceId(null);
        return false;
      }

      // Same tenant as sidebar — no override needed.
      if (peerCard.id === selectedInstanceIdRef.current) {
        setPeerOpsInstanceId(null);
        return true;
      }

      setPeerOpsInstanceId(peerCard.id);
      return true;
    },
    [instances],
  );

  /** Clear soft peer ops override (alias kept for existing Voltar / Root callers). */
  const restorePeerOpsPin = useCallback(() => {
    if (!peerOpsInstanceId) return false;
    setPeerOpsInstanceId(null);
    return true;
  }, [peerOpsInstanceId]);



  const clearSelectedInstance = useCallback(() => {
    // Org routes (/baas/usuarios, /baas/grupos) call clear on mount. If we always
    // bump dataEpoch, BaasConsoleRoute remounts children (key includes dataEpoch)
    // → clear again → infinite load loop.
    if (selectedInstanceIdRef.current == null) {
      writeStoredInstanceId(null);
      return;
    }

    setSelectedInstanceId(null);

    writeStoredInstanceId(null);

    setAccounts([]);

    setSelectedAccountId(null);

    setStoredBaasConsoleAccountId(null);

    selectedInstanceIdRef.current = null;

    setDataEpoch((epoch) => epoch + 1);

  }, []);



  const selectAccount = useCallback((accountId) => {

    const nextId = accountId ? String(accountId) : null;

    setSelectedAccountId(nextId);

    // Console-only — never write IB Carteira storage or fire IB_ACTIVE_ACCOUNT_CHANGED.
    setStoredBaasConsoleAccountId(nextId);

  }, []);



  const selectedAccount = useMemo(() => {
    if (selectedAccountId == null || selectedAccountId === "") return null;
    const wanted = String(selectedAccountId);
    return (
      accounts.find((account) => String(account.id) === wanted) ?? null
    );
  }, [accounts, selectedAccountId]);




  const createInstance = useCallback(

    async ({
      name,
      environment,
      isProduction = null,
      createHmlTwin = null,
      programCode = null,
      copyTeamFrom = null,
      ownerEntityId = null,
      description = null,
      countryCode = null,
      currencies = null,
      allowSkipKyc = null,
      allowedIps = null,
      receiveEmail = null,
      templateId = null,
      userAccessTemplateId = null,
      userAccessInviteTemplateId = null,
      rootAccountId = null,
      useVirtualRoot = null,
      registerSmtp = false,
      smtp = null,
      scopes = null,
      features = null,
      pixLimits = null,
      stablesLimits = null,
      efxLimits = null,
      cloneFromInstanceId = null,
    } = {}) => {

      void copyTeamFrom;

      const resolvedIsProduction =
        typeof isProduction === "boolean"
          ? isProduction
          : false;
      const parentLane = "production";
      const parentInstance = resolveBaasCreateParentInstance(instances, parentLane);

      if (!parentInstance) {

        const err = new Error(

          "An existing BaaS instance with create permission is required in this environment.",

        );

        err.name = "BaasConsoleError";

        err.code = "instance_create_parent_required";

        throw err;

      }



      const { instance: optimistic } = await createBaasInstance({

        name,

        environment: parentLane,

        isProduction: resolvedIsProduction,

        parentInstance,

        programCode: programCode || undefined,

        ownerEntityId: ownerEntityId || undefined,

        description,

        countryCode: countryCode || undefined,

        currencies: currencies || undefined,

        allowSkipKyc,

        allowedIps,

        receiveEmail,

        templateId,

        userAccessTemplateId,

        userAccessInviteTemplateId,

        rootAccountId,

        useVirtualRoot,

        registerSmtp,

        smtp,

        scopes,

        features,

        pixLimits,

        stablesLimits,

        efxLimits,

        cloneFromInstanceId,

      });



      let nextList = [];

      try {

        nextList = await refreshInstances();

      } catch {

        nextList = [];

      }



      const selectionId = optimistic?.id

        || buildBaasInstanceSelectionId(optimistic?.baasInstanceId, environment);

      const fromList =

        nextList.find((row) => row.id === selectionId) ||

        nextList.find(

          (row) =>

            Number(row.baasInstanceId) === Number(optimistic?.baasInstanceId) &&

            String(row.environment || "").toLowerCase() ===

              String(environment || "").toLowerCase(),

        ) ||

        nextList.find(

          (row) => Number(row.baasInstanceId) === Number(optimistic?.baasInstanceId),

        );



      const created = fromList || optimistic;

      if (!fromList && created) {

        setInstances((current) => {

          if (current.some((row) => row.id === created.id)) return current;

          return [...current, created];

        });

      }



      selectInstance(created.id);

      return created;

    },

    [instances, refreshInstances, selectInstance],

  );



  const updateInstance = useCallback(async (instanceId, data) => {
    const current = instances.find((row) => row.id === instanceId) ?? getBaasInstanceById(instanceId);
    if (!current) return null;

    // Existing instances must PATCH /v1/instances/{id} — never POST create.
    if (current.baasInstanceId) {
      await updateBaasInstance({
        instance: current,
        name: data?.name,
        isProduction: data?.isProduction,
        active: data?.active,
        createHmlTwin: data?.createHmlTwin,
        linkHmlInstanceId: data?.linkHmlInstanceId,
        unlinkHml: data?.unlinkHml,
      });

      try {
        await refreshInstances();
      } catch {
        // keep local optimistic patch below
      }
    }

    let updatedInstance = null;
    setInstances((list) =>
      list.map((instance) => {
        if (instance.id !== instanceId) return instance;
        updatedInstance = updateMockBaasInstance(instanceId, data, instance);
        return updatedInstance ?? instance;
      }),
    );

    return updatedInstance;
  }, [instances, refreshInstances]);



  const value = useMemo(

    () => ({

      instances,

      selectedInstance,

      selectedInstanceId,

      opsInstance,

      peerOpsInstanceId,

      listInstanceFilter,

      setListInstanceFilter,

      dataEpoch,

      loading,

      error,

      refreshInstances,

      selectInstance,

      selectInstanceForPeerOps,

      restorePeerOpsPin,

      clearPeerOpsPin,

      clearSelectedInstance,

      createInstance,

      updateInstance,

      accounts,

      accountsLoading,

      selectedAccount,

      selectedAccountId,

      selectAccount,

      refreshAccounts,

      getInstanceById: (instanceId) =>

        instances.find((instance) => instance.id === instanceId) ??

        getBaasInstanceById(instanceId),

    }),

    [

      instances,

      selectedInstance,

      selectedInstanceId,

      opsInstance,

      peerOpsInstanceId,

      listInstanceFilter,

      setListInstanceFilter,

      dataEpoch,

      loading,

      error,

      refreshInstances,

      selectInstance,

      selectInstanceForPeerOps,

      restorePeerOpsPin,

      clearPeerOpsPin,

      clearSelectedInstance,

      createInstance,

      updateInstance,

      accounts,

      accountsLoading,

      selectedAccount,

      selectedAccountId,

      selectAccount,

      refreshAccounts,

    ],

  );



  return (

    <BaasInstanceContext.Provider value={value}>

      {children}

    </BaasInstanceContext.Provider>

  );

};



export const useBaasInstance = () => {

  const context = useContext(BaasInstanceContext);

  if (!context) {

    throw new Error("useBaasInstance must be used within BaasInstanceProvider");

  }

  return context;

};


